/**
 * The mark and the navigation icons.
 *
 * Inline SVG, no icon library. Eight glyphs do not justify a dependency, a
 * tree-shaking configuration, or a second set of names to learn — and these are
 * the same paths the portal this replaces used, so the sidebar reads as the same
 * product rather than as a redesign nobody asked for.
 *
 * `currentColor` throughout: an icon that carries its own colour has to be told
 * about every state its label already handles, and the two drift.
 */
import { C } from '@ridgewill/brand';

type IconProps = { size?: number };

const line = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.5 } as const;

/** The Ridgewill ridge. Turquoise front peak, brand-blue rear.
 *
 *  THE MARK IS STILL THE MARK; it is now written in the tokens that say so.
 *  This used to hardcode both hexes, with a comment explaining that the mark
 *  does not follow the theme. Dark made that untrue rather than principled:
 *  #2B63E8 on the dark sidebar is 3.36:1, so the rear peak all but vanished
 *  and the logo read as a single turquoise triangle.
 *
 *  `C.turquoise` is deliberately the SAME colour in both palettes, so the front
 *  peak is unchanged and the fact that it never varies now lives in one place
 *  -- the palette -- instead of being restated here. `C.brand` lightens to
 *  #6E98FF in dark, which restores the rear peak to 6.30:1. */
export function Logo({ size = 24 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path d="M3 26 L11 11 L19 26 Z" fill={C.turquoise} />
      <path d="M13 26 L22 8 L31 26 Z" fill={C.brand} />
    </svg>
  );
}

export const OverviewIcon = ({ size = 18 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 18 18" {...line} aria-hidden="true">
    <rect x="2" y="2" width="5.5" height="5.5" rx="1.2" />
    <rect x="10.5" y="2" width="5.5" height="5.5" rx="1.2" />
    <rect x="2" y="10.5" width="5.5" height="5.5" rx="1.2" />
    <rect x="10.5" y="10.5" width="5.5" height="5.5" rx="1.2" />
  </svg>
);

export const UsersIcon = ({ size = 18 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 18 18" {...line} aria-hidden="true">
    <circle cx="9" cy="6" r="3" />
    <path d="M3.5 15.5 C3.5 11.5 14.5 11.5 14.5 15.5" />
  </svg>
);

/** Testers: a phone with a tick on it (RidgeWill/platform#56). */
export const TestersIcon = ({ size = 18 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 18 18" {...line} aria-hidden="true">
    <rect x="4.5" y="1.75" width="9" height="14.5" rx="2" />
    <path d="M6.9 9 L8.4 10.5 L11.1 7.6" />
  </svg>
);

/**
 * Agents: the Users silhouette with a square head.
 *
 * Deliberately the same shape at the same weight, differing in one detail. An
 * agent account IS a user account, and a wholly different glyph would say
 * otherwise; the corner is enough to tell the two nav entries apart at a
 * glance without implying the screens are unrelated.
 */
export const AgentIcon = ({ size = 18 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 18 18" {...line} aria-hidden="true">
    <rect x="6" y="3" width="6" height="6" rx="1" />
    <path d="M3.5 15.5 C3.5 11.5 14.5 11.5 14.5 15.5" />
  </svg>
);

/**
 * Security groups: two people inside a boundary.
 *
 * Deliberately NOT a third variation on the single figure `UsersIcon` draws —
 * the two sit next to each other in the nav, and a group icon that is "Users but
 * slightly different" is a row people click by mistake. The enclosure is the
 * whole idea being drawn: a set, not more people.
 */
export const GroupsIcon = ({ size = 18 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 18 18" {...line} aria-hidden="true">
    <rect x="1.6" y="3.2" width="14.8" height="11.6" rx="2.6" />
    <circle cx="6.6" cy="7.6" r="1.7" />
    <circle cx="11.6" cy="7.6" r="1.7" />
    <path d="M3.9 12.6 C3.9 10.4 9.3 10.4 9.3 12.6" />
    <path d="M8.9 12.6 C8.9 10.4 14.3 10.4 14.3 12.6" />
  </svg>
);

export const AppsIcon = ({ size = 18 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 18 18" {...line} aria-hidden="true">
    <rect x="2.5" y="2.5" width="9" height="9" rx="2" />
    <rect x="6.5" y="6.5" width="9" height="9" rx="2" />
  </svg>
);

export const DocIcon = ({ size = 18 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 18 18" {...line} aria-hidden="true">
    <path d="M4 2.5 H10.5 L14 6 V15.5 H4 Z" />
    <path d="M10 2.5 V6 H14" />
    <path d="M6.5 9.5 H11.5 M6.5 12 H11.5" />
  </svg>
);

export const ActivityIcon = ({ size = 18 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 18 18" {...line} aria-hidden="true">
    <polyline points="2,11 6,7 9,10 16,3" />
  </svg>
);

/** A speech bubble: the Support queue, people writing in (RidgeWill/platform#29). */
export const SupportIcon = ({ size = 18 }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 18 18"
    {...line}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 4.5h12a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H8l-3.5 3v-3H3a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1z" />
    <line x1="5.5" y1="8.5" x2="12.5" y2="8.5" />
  </svg>
);

/**
 * A gear with eight teeth: Support > Configuration, how the support desk is
 * set up. Teeth rather than rays (Jon, 2026-10-12: the rays read as a sun).
 */
/* A flag on a pole: Reports (support#87). The app's Report action uses the same mark. */
export const FlagIcon = ({ size = 18 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 18 18" {...line} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 16V2.75" />
    <path d="M4 3h8.5l-1.75 3.25L12.5 9.5H4" />
  </svg>
);

export const SettingsIcon = ({ size = 18 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 18 18" {...line} strokeLinejoin="round" aria-hidden="true">
    <path d="M14.52 7.58 L16.32 7.89 L16.32 10.11 L14.52 10.42 L13.91 11.90 L14.96 13.39 L13.39 14.96 L11.90 13.91 L10.42 14.52 L10.11 16.32 L7.89 16.32 L7.58 14.52 L6.10 13.91 L4.61 14.96 L3.04 13.39 L4.09 11.90 L3.48 10.42 L1.68 10.11 L1.68 7.89 L3.48 7.58 L4.09 6.10 L3.04 4.61 L4.61 3.04 L6.10 4.09 L7.58 3.48 L7.89 1.68 L10.11 1.68 L10.42 3.48 L11.90 4.09 L13.39 3.04 L14.96 4.61 L13.91 6.10 Z" />
    <circle cx="9" cy="9" r="2.3" />
  </svg>
);

/** An open book: the Knowledgebase, answers people can read for themselves. */
export const BookIcon = ({ size = 18 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 18 18" {...line} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 4.5C7.5 3.4 5.3 3 2.5 3.2v10.6c2.8-.2 5 .2 6.5 1.3 1.5-1.1 3.7-1.5 6.5-1.3V3.2C12.7 3 10.5 3.4 9 4.5z" />
    <path d="M9 4.5v10.6" />
  </svg>
);

/** A card with a stripe: Subscriptions, what people pay for (org-admin#162). */
export const SubscriptionsIcon = ({ size = 18 }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 18 18"
    {...line}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="2" y="4" width="14" height="10" rx="1.5" />
    <line x1="2" y1="7.5" x2="16" y2="7.5" />
    <line x1="4.5" y1="11" x2="7.5" y2="11" />
  </svg>
);

/** An envelope: the Notifications screen, which is email today (org-admin#131). */
export const MailIcon = ({ size = 18 }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 18 18"
    {...line}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="2" y="4" width="14" height="10.5" rx="1.6" />
    <polyline points="2.5,5 9,10 15.5,5" />
  </svg>
);

/** A pulse on a monitor. The same polyline family as ActivityIcon, because the
 *  two screens are neighbours and a wholly different metaphor would read as an
 *  unrelated section. */
export const HealthIcon = ({ size = 18 }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 18 18"
    {...line}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="1.5" y="3" width="15" height="12" rx="1.6" />
    <polyline points="4,9.5 6.5,9.5 8,6.5 10,12 11.5,9.5 14,9.5" />
  </svg>
);

/** Three stacked boxes with a status light each: the CI fleet's three hosts. */
export const FleetIcon = ({ size = 18 }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 18 18"
    {...line}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="2" y="2" width="14" height="4" rx="1" />
    <rect x="2" y="7" width="14" height="4" rx="1" />
    <rect x="2" y="12" width="14" height="4" rx="1" />
    <line x1="4.5" y1="4" x2="5" y2="4" />
    <line x1="4.5" y1="9" x2="5" y2="9" />
    <line x1="4.5" y1="14" x2="5" y2="14" />
  </svg>
);

export const DeveloperIcon = ({ size = 18 }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    {...line}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5.5 4.5 L2 8 L5.5 11.5" />
    <path d="M10.5 4.5 L14 8 L10.5 11.5" />
  </svg>
);
